import {
  Action,
  EmbedEvent,
  RuntimeFilterOp,
  SpotterEmbed,
} from "@thoughtspot/visual-embed-sdk"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"

import { useThoughtSpotInit } from "./useThoughtSpotInit"

const EMPTY_FILTERS = []

const SpotterAnalytics = ({
  thoughtspotHost,
  worksheetId,
  runtimeFilters = EMPTY_FILTERS,
}) => {
  const [error, setError] = useState(null)
  const [embedHeight, setEmbedHeight] = useState(600)
  const embedRef = useRef(null)
  const embedInstanceRef = useRef(null)

  useThoughtSpotInit(thoughtspotHost)

  const formattedFilters = useMemo(
    () =>
      runtimeFilters.map((filter) => ({
        columnName: filter.columnName,
        operator: RuntimeFilterOp[filter.operator],
        values: filter.values,
      })),
    [runtimeFilters]
  )

  useEffect(() => {
    const calculateHeight = () => {
      if (embedRef.current) {
        const rect = embedRef.current.getBoundingClientRect()
        const availableHeight = window.innerHeight - rect.top
        setEmbedHeight(Math.max(availableHeight, 400))
      }
    }

    calculateHeight()
    window.addEventListener("resize", calculateHeight)

    return () => {
      window.removeEventListener("resize", calculateHeight)
    }
  }, [])

  useEffect(() => {
    const initializeEmbed = async () => {
      try {
        if (embedRef.current && !embedInstanceRef.current) {
          const config = {
            frameParams: {
              width: "100%",
              height: "100%",
            },
            runtimeFilters: formattedFilters,
            hiddenActions: [
              Action.AddToFavorites,
              Action.Share,
              Action.RenameModalTitleDescription,
              Action.ExportTML,
              Action.EditTML,
              Action.UpdateTML,
              Action.Save,
              Action.SaveAsView,
              Action.MakeACopy,
              Action.Edit,
              Action.EditDetails,
              Action.Schedule,
              Action.SchedulesList,
              Action.Subscription,
              Action.ManageMonitor,
              Action.SyncToSlack,
              Action.SyncToTeams,
              Action.RequestVerification,
              Action.PreviewDataSpotter,
              Action.InConversationTraining,
            ],
          }

          if (worksheetId) {
            config.worksheetId = worksheetId
          }

          const embed = new SpotterEmbed(embedRef.current, config)

          embed.on(EmbedEvent.Error, (e) => {
            console.error("TS Spotter EmbedError:", e)
            setError("Failed to load AI search")
          })

          embed.on(EmbedEvent.Init, () => {
            console.log("TS Spotter Init")
          })

          embed.on(EmbedEvent.Load, () => {
            console.log("TS Spotter Loaded")
          })

          embed.on(EmbedEvent.EmbedHeight, (data) => {
            console.log("TS Spotter Height changed:", data)
            setEmbedHeight(data)
          })

          embedInstanceRef.current = embed
          await embed.render()
        }
      } catch (err) {
        console.error("Failed to initialize ThoughtSpot Spotter embed:", err)
        setError("Failed to initialize AI search")
      }
    }

    initializeEmbed()

    return () => {
      if (embedInstanceRef.current) {
        embedInstanceRef.current.destroy?.()
        embedInstanceRef.current = null
      }
    }
  }, [thoughtspotHost, worksheetId, formattedFilters])

  if (error) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ color: "red" }}>{error}</p>
      </div>
    )
  }

  return (
    <div
      ref={embedRef}
      className="thoughtspot-embed-container"
      style={{
        height: `${embedHeight}px`,
        width: "100%",
        overflow: "hidden",
      }}
    />
  )
}

SpotterAnalytics.propTypes = {
  thoughtspotHost: PropTypes.string.isRequired,
  worksheetId: PropTypes.string,
  runtimeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      columnName: PropTypes.string.isRequired,
      operator: PropTypes.string.isRequired,
      values: PropTypes.array.isRequired,
    })
  ),
}

export default SpotterAnalytics
